import { useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  GripVerticalIcon,
  PlusIcon,
  HashIcon,
  TableIcon,
  FileTextIcon,
} from "lucide-react";
import { AttributeKindEnum } from "@brewbase/db/types";
import { Input } from "@/components/ui/input";
import { kindOptions } from "@/components/template-attribute-item/AttributeKindSelector";
import type { BaseTemplateAttribute } from "./types";
import { useEditorConfig } from "./editor-config-context";

interface ScalarRowProps {
  attribute: BaseTemplateAttribute;
  /** First row in a consecutive scalar group */
  isFirst: boolean;
  /** First row of a non-first block — adds top spacing */
  isFirstInBlock: boolean;
  isSelected: boolean;
  isLocked: boolean;
  onSelect: (id: number) => void;
  onUpdateName: (name: string) => void;
  /** Position to insert before this row, null = no insert button */
  insertPosition: number | null;
  onInsert: (position: number, kind: AttributeKindEnum) => void;
}

const kindIconMap = new Map(
  kindOptions.map((k) => [k.value, { icon: k.icon, label: k.label }]),
);

function getUnit(attr: BaseTemplateAttribute): string | null {
  if (
    attr.kind === AttributeKindEnum.SINGLE_NUMBER &&
    attr.measurement_default_unit
  ) {
    return attr.measurement_default_unit;
  }
  if (
    attr.kind === AttributeKindEnum.COMPUTED_NUMBER &&
    attr.computed_number_result_display_unit
  ) {
    return attr.computed_number_result_display_unit;
  }
  return null;
}

function RowInsertMenu({
  position,
  onInsert,
}: {
  position: number;
  onInsert: (position: number, kind: AttributeKindEnum) => void;
}) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });

  const optClass =
    "flex items-center gap-1.5 w-full px-2 py-1 text-xs text-left text-muted-foreground hover:text-foreground hover:bg-muted transition-colors";

  return (
    <>
      <button
        className="absolute -left-7 top-0 -translate-y-1/2 z-20 flex items-center justify-center h-6 w-6 border border-border text-muted-foreground hover:text-foreground hover:border-foreground rounded-full bg-background transition-colors opacity-0 group-hover/row:opacity-100"
        onClick={(e) => {
          e.stopPropagation();
          setMenuPos({ x: e.clientX, y: e.clientY });
          setOpen(true);
        }}
      >
        <PlusIcon className="h-3.5 w-3.5" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-50" onClick={() => setOpen(false)} />
          <div
            className="fixed z-50 border border-border bg-background shadow-md py-1 min-w-[120px]"
            style={{ left: menuPos.x, top: menuPos.y }}
          >
            <button
              className={optClass}
              onClick={(e) => {
                e.stopPropagation();
                onInsert(position, AttributeKindEnum.SINGLE_NUMBER);
                setOpen(false);
              }}
            >
              <HashIcon className="h-3 w-3" />
              Attribute
            </button>
            <button
              className={optClass}
              onClick={(e) => {
                e.stopPropagation();
                onInsert(position, AttributeKindEnum.TABULAR_DATA);
                setOpen(false);
              }}
            >
              <TableIcon className="h-3 w-3" />
              Table
            </button>
            <button
              className={optClass}
              onClick={(e) => {
                e.stopPropagation();
                onInsert(position, AttributeKindEnum.INSTRUCTION);
                setOpen(false);
              }}
            >
              <FileTextIcon className="h-3 w-3" />
              Text
            </button>
          </div>
        </>
      )}
    </>
  );
}

export function ScalarRow({
  attribute,
  isFirst,
  isFirstInBlock,
  isSelected,
  isLocked: editorLocked,
  onSelect,
  onUpdateName,
  insertPosition,
  onInsert,
}: ScalarRowProps) {
  const { renderRowFlags, isGeneratedAttribute } = useEditorConfig();
  const isLocked = editorLocked || (isGeneratedAttribute?.(attribute) ?? false);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(attribute.name || "");

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: attribute.id.toString() });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  } as React.CSSProperties;

  const kindInfo = kindIconMap.get(attribute.kind);
  const unit = getUnit(attribute);

  const commitName = () => {
    setIsEditing(false);
    if (draft !== (attribute.name || "")) {
      onUpdateName(draft);
    }
  };

  const startEditing = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isLocked) {
      setDraft(attribute.name || "");
      setIsEditing(true);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-attribute-id={attribute.id}
      className={
        "group/row relative flex items-stretch cursor-pointer hover:bg-muted/30 transition-colors border border-border bg-background " +
        (isFirst ? "" : "-mt-px ") +
        (isFirstInBlock ? "mt-3 " : "") +
        (isSelected
          ? "border-l-2 border-l-[var(--farmless-green)] bg-olive-2 z-10"
          : "")
      }
      onClick={() => onSelect(attribute.id)}
    >
      {/* Insert button — positioned at top edge */}
      {!isLocked && insertPosition !== null && (
        <RowInsertMenu
          position={insertPosition}
          onInsert={(pos, kind) => {
            onInsert(pos, kind);
          }}
        />
      )}

      {/* Drag handle */}
      <div className="flex items-center px-1 shrink-0">
        {!isLocked && (
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab p-0.5 text-muted-foreground hover:text-foreground opacity-0 group-hover/row:opacity-100 transition-opacity"
            aria-label="Drag to reorder"
          >
            <GripVerticalIcon className="h-3 w-3" />
          </button>
        )}
      </div>

      {/* Name cell */}
      <div
        className="flex-1 min-w-0 flex items-center py-1.5 px-2 border-r border-border"
        onDoubleClick={startEditing}
      >
        {isEditing ? (
          <Input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitName}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitName();
              if (e.key === "Escape") {
                setDraft(attribute.name || "");
                setIsEditing(false);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            className="h-7 text-base font-medium px-1 -mx-1"
          />
        ) : (
          <span className="text-base font-medium truncate">
            {attribute.name || (
              <span className="text-muted-foreground italic">Untitled</span>
            )}
          </span>
        )}
      </div>

      {/* Kind + Unit cell */}
      <div className="flex items-center gap-1 px-3 py-1.5 w-[160px] shrink-0 border-r border-border text-sm text-muted-foreground">
        {kindInfo && (
          <>
            <span className="[&_svg]:h-3 [&_svg]:w-3">{kindInfo.icon}</span>
            <span className="truncate">
              {kindInfo.label}
              {unit && unit !== "1" ? ` · ${unit}` : ""}
            </span>
          </>
        )}
      </div>

      {/* Flags cell — entity-specific */}
      <div className="flex items-center gap-1.5 px-3 py-1.5 w-[120px] shrink-0 text-sm text-muted-foreground font-mono">
        {renderRowFlags(attribute)}
      </div>
    </div>
  );
}
